import { forwardRef } from "react";
import { cn } from "@/lib/utils";

export interface SpaceProps extends React.HTMLAttributes<HTMLDivElement> {
	direction?: "horizontal" | "vertical";
	size?: "small" | "middle" | "large" | number | [number, number];
	wrap?: boolean;
	align?: "start" | "end" | "center" | "baseline";
	split?: React.ReactNode;
}

const Space = forwardRef<HTMLDivElement, SpaceProps>(
	(
		{
			className,
			direction = "horizontal",
			size = "small",
			wrap = false,
			align,
			split,
			children,
			style,
			...props
		},
		ref,
	) => {
		const sizeMap = {
			small: 8,
			middle: 16,
			large: 24,
		};

		const getGap = () => {
			if (typeof size === "number") {
				return size;
			}
			if (Array.isArray(size)) {
				return size;
			}
			return sizeMap[size] || 8;
		};

		const gap = getGap();
		const gapStyle = Array.isArray(gap) ? { rowGap: gap[1], columnGap: gap[0] } : { gap };

		const alignMap = {
			start: "flex-start",
			end: "flex-end",
			center: "center",
			baseline: "baseline",
		};

		// Filter out null/undefined children and add split
		const items = Array.isArray(children)
			? children.filter((child) => child != null && child !== false)
			: children != null && children !== false
				? [children]
				: [];

		const renderItems = () => {
			if (!split) {
				return items;
			}

			return items.map((child, index) => (
				<>
					{index > 0 && <span className="flex-shrink-0">{split}</span>}
					{child}
				</>
			));
		};

		return (
			<div
				ref={ref}
				className={cn(
					"inline-flex",
					direction === "vertical" ? "flex-col" : "flex-row items-center",
					wrap && "flex-wrap",
					className,
				)}
				style={{
					...gapStyle,
					alignItems: align ? alignMap[align] : undefined,
					...style,
				}}
				{...props}
			>
				{renderItems()}
			</div>
		);
	},
);

Space.displayName = "Space";

// Compact version
const SpaceCompact = forwardRef<HTMLDivElement, SpaceProps>(
	({ className, children, ...props }, ref) => {
		return (
			<div
				ref={ref}
				className={cn(
					"inline-flex [&>*:not(:first-child)]:rounded-l-none [&>*:not(:last-child)]:rounded-r-none [&>*:not(:first-child)]:-ml-px",
					className,
				)}
				{...props}
			>
				{children}
			</div>
		);
	},
);

SpaceCompact.displayName = "SpaceCompact";

export { Space, SpaceCompact };
